import { Client, ClientRepresentationStatusEnum } from '@sr-sdks/api-sdk-axios';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { queryOptionsGetClientsPaginated } from '../api/workspaces/clientApi';
import FilteredClients from '../pages/Onboarding/ReviewClients/FilteredClients';
import { toTitleCase } from '../utils/helpers/common';
import AppLoader from './Loader';
import AppTabMenuModal from './navigation/AppTabMenuModal';

/**
 * Client review tabs. Since APP-2373 removed the onboarding client steps, its
 * only callers are the Dashboard and MSR3 Home alert modals, which supply their
 * own chrome and persist the decisions themselves.
 */
export interface IReviewClientsProps {
  clientWorkspaceId?: string;
  clientsList?: Client[];
}

export const clientInfoList = [
  'By confirming a client, you are acknowledging that you currently have a working relationship with this person OR you had a working relationship with this person in the past but not anymore. This includes any clients that you are entitled to make claims for.',
  'By declining a client, you are acknowledging that you have never had a working relationship with this person and cannot make claims on their behalf. You will be restricted from accessing further information about these clients.',
  'By not confirming or declining a client, this person will remain unconfirmed on your account. Please note you may be restricted from accessing further information about this person until an acknowledgement has been made via the homepage.',
];

const clientInfoByStatus: Record<ClientRepresentationStatusEnum, string> = {
  [ClientRepresentationStatusEnum.Confirmed]: clientInfoList[0],
  [ClientRepresentationStatusEnum.Declined]: clientInfoList[1],
  [ClientRepresentationStatusEnum.Unconfirmed]: clientInfoList[2],
};

type ClientsByStatus = Record<ClientRepresentationStatusEnum, Client[]>;

// Stable reference so a still-loading (or absent) query doesn't feed the
// effect below a fresh `[]` on every render — that would re-trigger the
// effect (and its `setClientsByStatus` call) every render, an infinite loop.
const NO_CLIENTS: Client[] = [];

export const ReviewClients = ({
  clientWorkspaceId,
  clientsList,
}: IReviewClientsProps) => {
  const { workspaceId } = useParams<{ workspaceId: string }>();

  const [clientsByStatus, setClientsByStatus] = useState<ClientsByStatus>({
    confirmed: [],
    declined: [],
    unconfirmed: [],
  });

  const [currentTab, setCurrentTab] = useState<ClientRepresentationStatusEnum>(
    ClientRepresentationStatusEnum.Confirmed,
  );

  const { data: clientsData, isLoading: isClientLoading } = useQuery({
    ...queryOptionsGetClientsPaginated({
      page: 1,
      pageSize: 100,
      sortBy: 'created:asc',
      workspaceId,
    }),
    // A caller that passes `clientWorkspaceId` supplies `clientsList` too, and
    // the effect below never reads this query then: skip the request and the
    // loader it would show.
    enabled: !clientWorkspaceId,
  });
  const clientsSet = clientsData?.items ?? NO_CLIENTS;

  useEffect(() => {
    if (clientWorkspaceId) {
      const clients = clientsList ?? [];

      setClientsByStatus({
        confirmed: clients.filter(
          (client) =>
            client.representationStatus ===
            ClientRepresentationStatusEnum.Confirmed,
        ),
        declined: clients.filter(
          (client) =>
            client.representationStatus ===
            ClientRepresentationStatusEnum.Declined,
        ),
        unconfirmed: clients.filter(
          (client) =>
            client.representationStatus ===
            ClientRepresentationStatusEnum.Unconfirmed,
        ),
      });

      // The caller owns the list (modal steps); do not overwrite it with the
      // store, which may hold another workspace's clients.
      return;
    }

    if (!workspaceId) return;

    const confirmedClients = clientsSet.filter(
      (client) =>
        client.representationStatus == ClientRepresentationStatusEnum.Confirmed,
    );

    const declinedClients = clientsSet.filter(
      (client) =>
        client.representationStatus == ClientRepresentationStatusEnum.Declined,
    );

    const unconfirmedClients = clientsSet.filter(
      (client) =>
        client.representationStatus ==
        ClientRepresentationStatusEnum.Unconfirmed,
    );

    const filterData = async () => {
      setClientsByStatus({
        confirmed: confirmedClients,
        declined: declinedClients,
        unconfirmed: unconfirmedClients,
      });
    };

    filterData();
  }, [clientWorkspaceId, clientsList, clientsSet, workspaceId]);

  if (isClientLoading) return <AppLoader.FlexLoader />;

  return (
    <div className="flex h-full flex-col gap-5 pt-5">
      <ClientTabs
        clientWorkspaceId={clientWorkspaceId}
        clientsByStatus={clientsByStatus}
        currentTab={currentTab}
        setCurrentTab={setCurrentTab}
      />
    </div>
  );
};

type ClientTabsProps = {
  clientWorkspaceId?: string;
  clientsByStatus: ClientsByStatus;
  currentTab: ClientRepresentationStatusEnum;
  setCurrentTab: (tab: ClientRepresentationStatusEnum) => void;
};

const ClientTabs = ({
  clientWorkspaceId,
  clientsByStatus,
  currentTab,
  setCurrentTab,
}: ClientTabsProps) => (
  <>
    <AppTabMenuModal.Group className="self-stretch [&_p]:hidden">
      {Object.values(ClientRepresentationStatusEnum).map((key) => (
        <AppTabMenuModal.Item
          key={key}
          label={toTitleCase(key)}
          currentTab={currentTab}
          itemCount={clientsByStatus[key].length}
          setCurrentTab={setCurrentTab}
          tabKey={key}
          tabToolTip={clientInfoByStatus[key]}
        />
      ))}
    </AppTabMenuModal.Group>

    <div
      className={clsx(
        'flex flex-1 flex-col items-start overflow-y-auto',
        clientWorkspaceId && '!w-full',
      )}
    >
      <div className="self-stretch border p-7">
        <FilteredClients
          clientInfo={clientInfoByStatus[currentTab]}
          clients={clientsByStatus[currentTab]}
        />
      </div>
    </div>
  </>
);
